import React, { useState } from 'react';
import { AlertCircle, RotateCw, WifiOff } from 'lucide-react';
import { cn } from '@/lib/utils';
import './primitives.css';

/**
 * A load failure, shown as a failure (never as an empty or "all clear" state).
 *
 *   <ErrorState title="Couldn't load breaches" error={err} onRetry={reload} />
 *
 *   error:   an axios error / Error / string. Network errors get an
 *            "API unreachable" message; HTTP errors show their status.
 *   onRetry: shows a Retry button (may return a promise: shows a pending state)
 *   variant: 'block' (default, centered) | 'compact' | 'inline' (one-line banner)
 */
export function describeError(error) {
  if (!error) return null;
  if (typeof error === 'string') return error;
  const status = error.response?.status;
  const detail = error.response?.data?.detail;
  if (!error.response && (error.code === 'ERR_NETWORK' || error.message === 'Network Error')) {
    return 'The IntelliSecure API could not be reached. Check your connection and try again.';
  }
  if (status === 401 || status === 403) return 'Your session does not have access to this. Try signing in again.';
  if (status === 429) return 'Too many requests right now. Wait a moment and retry.';
  if (typeof detail === 'string' && detail.length < 200) return detail;
  if (status) return `The server returned an error (HTTP ${status}).`;
  return error.message || 'Something went wrong.';
}

export default function ErrorState({
  title = "Couldn't load this",
  description,
  error,
  onRetry,
  retryLabel = 'Retry',
  variant = 'block',
  className,
  children,
}) {
  const [retrying, setRetrying] = useState(false);
  const offline = error && !error.response && (error.code === 'ERR_NETWORK' || error.message === 'Network Error');
  const Icon = offline ? WifiOff : AlertCircle;
  const message = description ?? describeError(error);

  const retry = async () => {
    if (!onRetry || retrying) return;
    try {
      const result = onRetry();
      if (result && typeof result.then === 'function') {
        setRetrying(true);
        await result;
      }
    } catch {
      /* the caller re-renders with the new error */
    } finally {
      setRetrying(false);
    }
  };

  return (
    <div
      role="alert"
      className={cn(
        'is-state is-state--error',
        variant === 'compact' && 'is-state--compact',
        variant === 'inline' && 'is-state--inline',
        className
      )}
      data-testid="error-state"
    >
      <span className="is-state-icon" aria-hidden="true">
        <Icon size={variant === 'inline' ? 14 : 18} />
      </span>
      <div>
        <div className="is-state-title">{title}</div>
        {message && <div className="is-state-text">{message}</div>}
      </div>
      {(onRetry || children) && (
        <div className="is-state-actions">
          {onRetry && (
            <button type="button" className="is-btn is-btn--secondary is-btn--sm" onClick={retry} disabled={retrying}>
              <RotateCw className={retrying ? 'is-spinner' : undefined} aria-hidden="true" />
              {retrying ? 'Retrying…' : retryLabel}
            </button>
          )}
          {children}
        </div>
      )}
    </div>
  );
}
